/**
 * Utility to detect if a search query is a warehouse location
 */

/**
 * Check if query matches a pallet location pattern (P1 to P200)
 * @param {string} query - Search query
 * @returns {boolean}
 */
function isPalletLocation(query) {
  if (!query) return false;
  const trimmed = query.trim().toUpperCase();
  
  // Match P1 to P200 (P followed by 1-3 digits, max 200)
  const match = trimmed.match(/^P(\d{1,3})$/);
  if (!match) return false;
  
  const number = parseInt(match[1], 10);
  return number >= 1 && number <= 200;
}

/**
 * Check if query matches a warehouse location pattern (Z-01 to S-01)
 * Format: [LETTER]-[NUMBER]-[LETTER]-[NUMBER]
 * First letter: Z down to S (no P or below S)
 * @param {string} query - Search query
 * @returns {boolean}
 */
function isWarehouseLocation(query) {
  if (!query) return false;
  const trimmed = query.trim().toUpperCase();

  if (/^Z-\d{2}-END-RACK$/.test(trimmed)) return true;
  if (/^[XZ]-\d{2}-FLOOR$/.test(trimmed)) return true;
  
  // Match pattern variations:
  // Z-01 (simple)
  // Z-01-B (aisle letter)
  // Z-01-B-01 (full location)
  // First part must be [LETTER]-[NUMBER]
  // Valid letters: Z, Y, X, W, V, U, T, S (no P, O, N, etc.)
  const match = trimmed.match(/^([ZYXWVUTS])-(\d{2})(-[A-Z](-\d{2})?)?$/);
  if (!match) return false;
  
  const firstLetter = match[1];
  const firstNumber = parseInt(match[2], 10);
  
  // Verify first letter is between Z and S (inclusive)
  const validLetters = ['Z', 'Y', 'X', 'W', 'V', 'U', 'T', 'S'];
  if (!validLetters.includes(firstLetter)) return false;
  
  // Verify number is valid (01-99)
  if (firstNumber < 1 || firstNumber > 99) return false;
  
  return true;
}

/**
 * Main function to detect if query is any type of location
 * @param {string} query - Search query
 * @returns {boolean}
 */
export function isLocationQuery(query) {
  if (!query || typeof query !== 'string') return false;
  return isPalletLocation(query) || isWarehouseLocation(query);
}

/**
 * Get location type for display purposes
 * @param {string} query - Search query
 * @returns {string|null} - 'pallet' or 'warehouse' or null
 */
export function getLocationType(query) {
  if (isPalletLocation(query)) return 'pallet';
  if (isWarehouseLocation(query)) return 'warehouse';
  return null;
}

/**
 * Normalize location for API query (trim and uppercase)
 * @param {string} query - Search query
 * @returns {string}
 */
export function normalizeLocation(query) {
  return query.trim().toUpperCase();
}
